/**
 * Confidence Service
 * Manages confidence score calculation and updates
 */

/**
 * Calculate new confidence score based on multiple factors
 * @param {Object} params
 * @param {number} params.baseConfidence - Initial confidence from classification
 * @param {number} params.videoFrequency - How often this pattern appears in the video (0-1)
 * @param {number} params.channelFrequency - How often this pattern appears across channel (0-1)
 * @param {number} params.userValidation - User validation score (-1 to 1)
 * @returns {number} Updated confidence score (0-1)
 */
export function calculateConfidence({
  baseConfidence = 0.5,
  videoFrequency = 0,
  channelFrequency = 0,
  userValidation = 0
}) {
  // Weights for each factor
  const VIDEO_WEIGHT = 0.05;
  const CHANNEL_WEIGHT = 0.03;
  const USER_WEIGHT = 0.1;

  const newConfidence = 
    baseConfidence +
    (videoFrequency * VIDEO_WEIGHT) +
    (channelFrequency * CHANNEL_WEIGHT) +
    (userValidation * USER_WEIGHT);

  // Clamp between 0 and 1
  return Math.min(1, Math.max(0, newConfidence));
}

/**
 * Calculate video frequency for an intent
 * @param {string} intent - Intent type
 * @param {Object} videoContext - Video context data
 * @returns {number} Frequency score (0-1)
 */
export function calculateVideoFrequency(intent, videoContext) {
  if (!videoContext || !videoContext.topIntents) {
    return 0;
  }

  const intentCount = videoContext.topIntents[intent] || 0;
  const totalIntents = Object.values(videoContext.topIntents).reduce((sum, count) => sum + count, 0);

  if (totalIntents === 0) return 0;

  return intentCount / totalIntents;
}

/**
 * Calculate channel frequency for an intent
 * @param {string} intent - Intent type
 * @param {Object} channelContext - Channel context data
 * @returns {number} Frequency score (0-1)
 */
export function calculateChannelFrequency(intent, channelContext) {
  if (!channelContext || !channelContext.recurringIntents) {
    return 0;
  }

  const intentCount = channelContext.recurringIntents[intent] || 0;
  const totalIntents = Object.values(channelContext.recurringIntents).reduce((sum, count) => sum + count, 0);

  if (totalIntents === 0) return 0;

  // Normalize to 0-1 scale (assume 10+ occurrences = 1.0)
  return Math.min(1, intentCount / 10);
}

/**
 * Calculate entity match boost
 * @param {string[]} commentEntities - Entities in the comment
 * @param {Object} videoContext - Video context
 * @param {Object} channelContext - Channel context
 * @returns {number} Boost amount (0-0.15)
 */
export function calculateEntityMatchBoost(commentEntities, videoContext, channelContext) {
  if (!commentEntities || commentEntities.length === 0) {
    return 0;
  }

  let matchCount = 0;
  let totalKnownEntities = 0;

  // Check video context friction themes
  if (videoContext?.topFrictionThemes) {
    const frictionEntities = Object.keys(videoContext.topFrictionThemes);
    totalKnownEntities += frictionEntities.length;
    matchCount += commentEntities.filter(e => frictionEntities.includes(e)).length;
  }

  // Check channel context friction themes
  if (channelContext?.historicalFriction) {
    const channelFrictionEntities = Object.keys(channelContext.historicalFriction);
    totalKnownEntities += channelFrictionEntities.length;
    matchCount += commentEntities.filter(e => channelFrictionEntities.includes(e)).length;
  }

  if (totalKnownEntities === 0) return 0;

  // Return boost proportional to match rate (max 0.15)
  return Math.min(0.15, (matchCount / totalKnownEntities) * 0.15);
}

/**
 * Determine if confidence should be decreased based on context contradiction
 * @param {string} intent - Predicted intent
 * @param {Object} videoContext - Video context
 * @param {Object} channelContext - Channel context
 * @returns {number} Penalty amount (0 to -0.2)
 */
export function calculateContradictionPenalty(intent, videoContext, channelContext) {
  let penalty = 0;

  // Check if intent contradicts dominant video patterns
  if (videoContext?.topIntents) {
    const topIntent = Object.keys(videoContext.topIntents).reduce((a, b) => 
      videoContext.topIntents[a] > videoContext.topIntents[b] ? a : b
    , '');
    
    const topIntentCount = videoContext.topIntents[topIntent] || 0;
    const currentIntentCount = videoContext.topIntents[intent] || 0;

    // If this intent is rare compared to dominant pattern
    if (topIntentCount > 0 && currentIntentCount / topIntentCount < 0.2) {
      penalty -= 0.05;
    }
  }

  // Check if intent contradicts channel patterns
  if (channelContext?.recurringIntents) {
    const channelIntentCount = channelContext.recurringIntents[intent] || 0;
    const totalChannelIntents = Object.values(channelContext.recurringIntents)
      .reduce((sum, count) => sum + count, 0);

    // If this intent is very rare for the channel
    if (totalChannelIntents > 0 && channelIntentCount / totalChannelIntents < 0.1) {
      penalty -= 0.05;
    }
  }

  return Math.max(-0.2, penalty);
}

/**
 * Get user validation score from user signals
 * @param {Array} userSignals - Array of user signal objects for this comment
 * @returns {number} Validation score (-1 to 1)
 */
export function getUserValidationScore(userSignals) {
  if (!userSignals || userSignals.length === 0) {
    return 0;
  }

  let score = 0;

  userSignals.forEach(signal => {
    switch (signal.actionType) {
      case 'favorite':
        score += 0.3;
        break;
      case 'export':
        score += 0.2;
        break;
      case 'clicked_action':
        score += 0.1;
        break;
      case 'reclassified':
        // If user reclassified, it's negative validation
        score -= 0.5;
        break;
      default:
        break;
    }
  });

  // Clamp between -1 and 1
  return Math.min(1, Math.max(-1, score));
}











